import { sendWarning } from '@components/Noti';
import { customButtonDefault } from '@modules/edit';
import { deleteHotspots } from '@modules/hotspot/actions/deleteHotspots';
import { deleteMarkers } from '@modules/sandTable/actions/deleteMarkers';
import {
  $categoryList,
  $categoryListApi,
  $categoryMap,
  $categoryMapApi,
  $curSceneId,
  $sceneList,
  $sceneMap,
  $sceneMapApi,
  $selectedPath,
  $selectedPathApi,
  $subCategoryList,
  $tourData,
  $tourDataApi,
} from '@stores/dataStores';
import {
  type ESceneMap,
  FeatureType,
  type GeneralID,
  type ST,
  type TourData,
} from '@types';
import { createEvent, sample } from 'effector';
import isNumeric from 'validator/es/lib/isNumeric';
import { krpRemoveHotspot } from '../../hotspot';
import {
  isMapFeatureMarker,
  isSTMarkerScene,
} from '../../sandTable/utils/markerTypeCheck';
import {
  isSTImage,
  isSTMap,
  isSTMatrix,
} from '../../sandTable/utils/sandTableTypeCheck';
import { getAllCategoryIds, getAllSceneIds, removeCategoryId } from './helpers';
import {
  selectCategory,
  selectScene,
  selectSubCategory,
} from './selectActions';

const includeUsedScene = ({
  tourData,
  idList,
  isGroup,
}: {
  tourData: Partial<TourData>;
  idList: GeneralID[];
  isGroup?: boolean;
}) => {
  const btnLinked =
    tourData.customButton?.reduce((prev: GeneralID[], cur) => {
      cur.children.forEach((x) => {
        if (x.code === FeatureType.scene && idList.includes(x.sceneId)) {
          prev.push(x.sceneId);
        }
      });
      return prev;
    }, []) ?? [];

  const initLinked =
    tourData.categoryConfig?.initScene &&
    idList.includes(tourData.categoryConfig.initScene);

  if (!btnLinked.length && !initLinked) return false;
  if (btnLinked.length) {
    if (isGroup) {
      sendWarning(
        '分组中有场景被设置为自定义按钮场景切换的目标场景，请取消后再删除'
      );
    } else {
      sendWarning(
        '当前场景被设置为自定义按钮场景切换的目标场景，请取消后再删除'
      );
    }
  }
  if (initLinked) {
    if (isGroup) {
      sendWarning('分组中有场景被设置为初始场景，请取消后再删除');
    } else {
      sendWarning('当前场景被设置为初始场景，请取消后再删除');
    }
  }
  return true;
};

/**
 * v5.8.3 改为传入id与parentId的组合, 兼容从搜索结果删除
 */
export const deleteScene =
  createEvent<
    {
      sceneId: GeneralID;
      categoryId: number;
    }[]
  >();
sample({
  source: {
    sceneList: $sceneList,
    sceneMap: $sceneMap,
    tourData: $tourData,
    currentSceneId: $curSceneId,
    selectedPath: $selectedPath,
  },
  clock: deleteScene,
  fn: (source, clock) => ({ ...source, payload: clock }),
}).watch(({ sceneList, sceneMap, tourData, currentSceneId, payload }) => {
  if (!payload.length) {
    throw new Error('未传入需要删除场景的id');
  }
  if (sceneMap.size <= payload.length) {
    sendWarning('无法删除，作品必须至少包含一个场景');
    return;
  }
  const idList = payload.map((x) => x.sceneId);
  if (includeUsedScene({ idList, tourData })) {
    return;
  }
  if (idList.includes(currentSceneId as GeneralID)) {
    // 如果删除的包含当前场景
    const nextId = sceneList.filter((_id) => !idList.includes(_id))[0] ?? null;
    selectScene(nextId);
  }
  $categoryMapApi.update((draft) => {
    payload.forEach((x) => {
      const cItem = draft.get(x.categoryId);
      if (!cItem) return;
      cItem.sceneIds = cItem.sceneIds.filter((id) => id !== x.sceneId);
    });
  });
  $sceneMapApi.update((draft) => {
    payload.forEach((x) => {
      draft.delete(x.sceneId);
    });
  });
  deleteLinkedHotspot({ idList, sceneId: currentSceneId, sceneMap });
  deleteSandTableMarkers({ idList, sandtableList: tourData.sandBox });
  deleteRotateTargetId({ idList });
  deleteVipViewScenes({ idList });
  deleteLinkedLayerComponent({ idList });
});

export const deleteSubCategory = createEvent<{
  subId: number;
  parentId?: number;
}>();
sample({
  source: {
    path: $selectedPath,
    map: $categoryMap,
    subCateList: $subCategoryList,
    sceneMap: $sceneMap,
    tourData: $tourData,
  },
  clock: deleteSubCategory,
  fn: (source, clock) => ({ ...source, ...clock }),
}).watch(({ path, map, subCateList, sceneMap, tourData, subId, parentId }) => {
  const categoryId = parentId ?? path.categoryPath[0];
  const { sceneIds } = map.get(subId)!;
  if (subCateList.length <= 1) {
    // 如果删除的是唯一一个二级分组，场景列表移到一级分组下
    $categoryMapApi.update((draft) => {
      const category = draft.get(categoryId)!;
      category.categoryIds = [];
      category.sceneIds = sceneIds;
      draft.delete(subId);
    });
    $selectedPathApi.update((draft) => {
      draft.categoryPath = [categoryId];
    });
  } else {
    if (map.get(subId)!.sceneIds.length >= sceneMap.size) {
      sendWarning('无法删除，作品必须至少包含一个场景');
      return;
    }
    if (includeUsedScene({ idList: sceneIds, tourData, isGroup: true })) {
      return;
    }
    if (subId === path.categoryPath[1]) {
      const nextId = subCateList.filter((_id) => _id !== subId)[0] ?? null;
      selectSubCategory(nextId);
    }
    $categoryMapApi.update((draft) => {
      removeCategoryId(subId, draft.get(categoryId));
      draft.delete(subId);
    });
    $sceneMapApi.update((draft) => {
      sceneIds.forEach((_id) => {
        draft.delete(_id);
      });
    });
    deleteLinkedHotspot({ idList: sceneIds, sceneId: path.id, sceneMap });
    deleteSandTableMarkers({
      idList: sceneIds,
      sandtableList: tourData.sandBox,
    });
    deleteRotateTargetId({ idList: sceneIds });
    deleteVipViewScenes({ idList: sceneIds });
    deleteLinkedLayerComponent({ idList: sceneIds });
  }
});

export const deleteCategory = createEvent<number>();
sample({
  source: {
    path: $selectedPath,
    map: $categoryMap,
    cateList: $categoryList,
    sceneMap: $sceneMap,
    tourData: $tourData,
  },
  clock: deleteCategory,
  fn: (source, cateId) => ({ ...source, cateId }),
}).watch(({ path, map, cateList, sceneMap, tourData, cateId }) => {
  const allSceneIds = getAllSceneIds(map, cateId);
  if (allSceneIds.length >= sceneMap.size) {
    sendWarning('无法删除，作品必须至少包含一个场景');
    return;
  }
  if (includeUsedScene({ idList: allSceneIds, tourData, isGroup: true })) {
    return;
  }
  if (cateId === path.categoryPath[0]) {
    const nextId = cateList.filter((_id) => _id !== cateId)[0] ?? null;
    selectCategory(nextId);
  }
  $categoryListApi.update((draft) => {
    const index = draft.findIndex((_id) => _id === cateId);
    if (index >= 0) {
      draft.splice(index, 1);
    }
  });
  $categoryMapApi.update((draft) => {
    draft.delete(cateId);
    getAllCategoryIds(map, cateId).forEach((_id) => {
      draft.delete(_id);
    });
  });
  $sceneMapApi.update((draft) => {
    allSceneIds.forEach((_id) => {
      draft.delete(_id);
    });
  });
  deleteLinkedHotspot({ idList: allSceneIds, sceneId: path.id, sceneMap });
  deleteSandTableMarkers({
    idList: allSceneIds,
    sandtableList: tourData.sandBox,
  });
  deleteRotateTargetId({ idList: allSceneIds });
  deleteVipViewScenes({ idList: allSceneIds });
  deleteLinkedLayerComponent({ idList: allSceneIds });
});

const deleteLinkedHotspot = ({
  idList,
  sceneId,
  sceneMap,
}: {
  idList: GeneralID[];
  sceneId: GeneralID | null;
  sceneMap: ESceneMap;
}) => {
  sceneMap.forEach((x) => {
    if (idList.includes(x.id)) return;
    const targetSet: Set<string> = new Set();
    (x.hotspot ?? []).forEach((item) => {
      if (
        item.data.code === FeatureType.scene &&
        idList.includes(item.data.sceneId)
      ) {
        targetSet.add(item.id);
        if (x.id === sceneId) {
          krpRemoveHotspot(item.id);
        }
      }
    });
    deleteHotspots({
      hotspotIds: targetSet,
      sceneId: x.id,
    });
  });
};

/**
 * 删除场景时关联删除沙盘标记
 */
const deleteSandTableMarkers = ({
  idList,
  sandtableList = [],
}: { idList: GeneralID[]; sandtableList?: ST[] }) => {
  sandtableList.forEach((item) => {
    if (isSTMap(item)) {
      const markerIdList = item.markers
        .filter((marker) => {
          if (isSTMarkerScene(marker) && !!marker.data.sceneId) {
            return idList.includes(marker.data.sceneId);
          }
          if (
            isMapFeatureMarker(marker) &&
            marker.data.code === FeatureType.scene
          ) {
            return idList.includes(marker.data.sceneId);
          }
          return false;
        })
        .map((x) => x.id);
      deleteMarkers({
        sandTableId: item.id,
        markerIds: new Set(markerIdList),
      });
    } else if (isSTImage(item) || isSTMatrix(item)) {
      const markerIdList = item.markers
        .filter((marker) => {
          if (isSTMarkerScene(marker)) {
            return idList.includes(marker.data.sceneId!);
          }
          return false;
        })
        .map((x) => x.id);
      deleteMarkers({
        sandTableId: item.id,
        markerIds: new Set(markerIdList),
      });
    }
  });
};

const deleteRotateTargetId = ({ idList }: { idList: GeneralID[] }) => {
  $sceneMapApi.update((draft) => {
    draft.forEach((x) => {
      const autoRotate = x?.config?.autoRotate;
      if (
        autoRotate?.targetSceneId &&
        idList.includes(autoRotate.targetSceneId)
      ) {
        autoRotate.targetSceneId = '';
      }
    });
  });
};

/**
 * 删除场景时关联删除付费观看场景
 */
const deleteVipViewScenes = ({ idList }: { idList: GeneralID[] }) => {
  $tourDataApi.update((draft) => {
    const idString = draft.config.vipView?.sceneList ?? '';
    if (!idString) return;
    const list = idString.split(',').map((id) => {
      if (isNumeric(id)) return Number(id);
      return id;
    });
    const newList = list.filter((id) => !idList.includes(id));
    draft.config.vipView!.sceneList = newList.join(',');
  });
};

/**
 * 删除场景时如果图层功能为切换场景,目标为该场景,则图层功能重置
 */
const deleteLinkedLayerComponent = ({ idList }: { idList: GeneralID[] }) => {
  $sceneMapApi.update((draft) => {
    draft.forEach((x) => {
      const list = x.config.components ?? [];
      x.config.components = list.map((item) => {
        if (
          item.data.code === FeatureType.scene &&
          idList.includes(item.data.sceneId)
        ) {
          return {
            ...item,
            data: customButtonDefault(FeatureType.noFunc),
          };
        }
        return item;
      });
    });
  });
};
